import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, ShieldAlert, Search, Database, Activity, 
  ChevronRight, Clock, Globe, Cpu, User, ArrowRight,
  Filter, RotateCcw, X, Terminal, ShieldX, Ghost, ServerCrash
} from 'lucide-react';
import api from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';

const ERROR_TYPES = [
  { id: 'all', label: 'All Reports', icon: Activity, color: 'text-[#0176D3]' },
  { id: 'authorization', label: 'Authorization', icon: ShieldX, color: 'text-[#BA0517]' },
  { id: 'server', label: 'Server Critical', icon: ServerCrash, color: 'text-[#0176D3]' },
  { id: 'notfound', label: '404 Missing', icon: Ghost, color: 'text-[#DD7A01]' }
];

export default function SystemErrors() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [type, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/error-logs?type=${type}&page=${page}`);
      if (res.data.success) {
        setLogs(res.data.logs);
        setTotalPages(res.data.pagination.pages);
      }
    } catch (err) {
      alertService.error('Couldn\'t load errors', 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (code) => {
    if (code >= 500) return 'text-[#C53030] bg-[#FFF5F5] border-[#FC8181]';
    if (code >= 400) return 'text-[#B7791F] bg-[#FFFFF0] border-[#F6E05E]';
    return 'text-[#3182CE] bg-[#EBF8FF] border-[#90CDF4]';
  };

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader
        title="System Errors"
        icon={Terminal}
        subtitle="Audit API errors, security events, and server-side issues in real time."
      />
      <div className="mb-4 flex">
        <div className="flex bg-[#F3F2F2] p-1 rounded-md border border-[#D8DDE6]">
           {ERROR_TYPES.map(t => (
             <button
               key={t.id}
               onClick={() => { setType(t.id); setPage(1); }}
               className={cn(
 "flex items-center gap-2 px-3 h-8 rounded text-[13px] font-semibold transition-colors",
 type === t.id ? "bg-white text-[#080707] shadow-sm" : "text-[#444444] hover:text-[#080707]"
 )}
             >
               <t.icon size={14} className={cn(type === t.id ? "text-[#080707]" : t.color)} />
               {t.label}
             </button>
           ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
         <Card className="bg-white border-[#E5E5E5] overflow-hidden rounded-lg p-0 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="overflow-x-auto">
               <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead>
                     <tr className="bg-[#F4F6F9] border-b border-[#D8DDE6]">
                        <th className="px-4 py-2.5 text-[11px] font-semibold text-[#706E6B] uppercase ">Timestamp</th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold text-[#706E6B] uppercase text-center">Status</th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold text-[#706E6B] uppercase ">Method / Path</th>
                        <th className="px-4 py-2.5 text-[11px] font-semibold text-[#706E6B] uppercase ">Organization</th>
                        <th className="px-4 py-2.5"></th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-[#D8DDE6]">
                     {loading ? (
                        [...Array(5)].map((_, i) => (
                           <tr key={i} className="animate-pulse">
                              <td colSpan={5} className="px-6 py-6"><div className="h-4 bg-[#F3F2F2] rounded-md w-full"></div></td>
                           </tr>
                        ))
                     ) : logs.length === 0 ? (
                        <tr>
                           <td colSpan={5} className="px-6 py-20 text-center">
                              <div className="flex flex-col items-center gap-4 text-[#706E6B]">
                                 <ShieldAlert size={32} />
                                 <div className="text-[13px] font-semibold">No anomalies detected in current buffer</div>
                              </div>
                           </td>
                        </tr>
                     ) : logs.map(log => (
                        <tr key={log._id} className="hover:bg-[#F7F9FB] transition-colors cursor-pointer" onClick={() => setSelectedError(log)}>
                           <td className="px-4 py-2.5">
                              <div className="text-[13px] font-bold text-[#080707] mb-0.5">{new Date(log.timestamp).toLocaleTimeString()}</div>
                              <div className="text-[12px] text-[#706E6B]">{new Date(log.timestamp).toLocaleDateString()}</div>
                           </td>
                           <td className="px-4 py-2.5 text-center">
                              <span className={cn(
 "px-2 py-0.5 rounded-sm text-[12px] font-bold border",
 getStatusColor(log.statusCode)
 )}>
                                 {log.statusCode}
                              </span>
                           </td>
                           <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                 <span className="text-[11px] font-bold text-[#0176D3] uppercase border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm bg-[#E0F2FE]">{log.method}</span>
                                 <span className="text-[13px] text-[#444444] truncate max-w-sm">{log.path}</span>
                              </div>
                           </td>
                           <td className="px-4 py-2.5">
                              {log.userId ? (
                                 <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-md bg-[#E0F2FE] border border-[#BAE6FD] flex items-center justify-center text-[#0176D3]">
                                       <User size={14} />
                                    </div>
                                    <div>
                                       <div className="text-[13px] font-bold text-[#080707]">{log.userId.companyName}</div>
                                       <div className="text-[12px] text-[#706E6B]">{log.userId.email}</div>
                                    </div>
                                 </div>
                              ) : (
                                 <span className="text-[13px] text-[#706E6B] font-semibold italic">System Anonymous</span>
                              )}
                           </td>
                           <td className="px-4 py-2.5 text-right">
                              <button className="w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#444444] hover:text-[#080707] hover:bg-[#F3F2F2] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                                 <Terminal size={14} />
                              </button>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
               <div className="px-6 py-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex items-center justify-between">
                  <div className="text-[12px] font-semibold text-[#706E6B]">Page {page} of {totalPages}</div>
                  <div className="flex items-center gap-2">
                     <button 
                       className="px-3 py-1.5 rounded whitespace-nowrap border border-[#C9C9C9] bg-white text-[13px] font-semibold text-[#444444] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F3F2F2]"
                       disabled={page <= 1} 
                       onClick={() => setPage(p => p - 1)}
                     >
                        Previous
                     </button>
                     <button 
                       className="px-3 py-1.5 rounded whitespace-nowrap border border-[#C9C9C9] bg-white text-[13px] font-semibold text-[#0176D3] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#F3F2F2]"
                       disabled={page >= totalPages} 
                       onClick={() => setPage(p => p + 1)}
                     >
                        Next
                     </button>
                  </div>
               </div>
            )}
         </Card>

         {/* Diagnostic Metrics */}
         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
               { label: 'Authorization Success Rate', value: '99.4%', status: 'Healthy', icon: ShieldAlert, color: 'text-[#16A34A]', bg: 'bg-[#DCFCE7]' },
               { label: 'Critical Heap Memory', value: '142 MB', status: 'Stable', icon: Cpu, color: 'text-[#0176D3]', bg: 'bg-[#E0F2FE]' },
               { label: 'Response Latency', value: '14ms', status: 'Active', icon: Activity, color: 'text-[#00B5D8]', bg: 'bg-[#E6FFFA]' }
            ].map((s, i) => (
               <Card key={i} className="p-6 bg-white border-[#D8DDE6] rounded-md shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                     <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", s.bg, s.color)}>
                        <s.icon size={20} strokeWidth={2} />
                     </div>
                     <Badge variant="outline" className="border-[#D8DDE6] text-[#706E6B] text-[11px] font-semibold">{s.status}</Badge>
                  </div>
                  <div className="space-y-0.5">
                     <div className="text-[12px] font-semibold text-[#706E6B]">{s.label}</div>
                     <div className="text-2xl font-bold text-[#080707]">{s.value}</div>
                  </div>
               </Card>
            ))}
         </div>
      </div>

      {/* Error Detail Modal */}
      {selectedError && (
         <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
            <Card className="w-full max-w-3xl bg-white border-[#E5E5E5] p-0 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
               <div className="px-6 py-4 bg-[#F4F6F9] border-b border-[#D8DDE6] flex items-center justify-between">
                  <div className="flex items-center gap-4">
                     <div className="w-10 h-10 rounded-md bg-[#FFF5F5] border border-[#FC8181] flex items-center justify-center text-[#C53030]">
                        <Terminal size={20} />
                     </div>
                     <div>
                        <h2 className="text-lg font-bold text-[#080707] leading-tight">Diagnostic Details</h2>
                        <p className="text-[12px] text-[#706E6B] font-mono">Log ID: {selectedError._id}</p>
                     </div>
                  </div>
                  <button onClick={() => setSelectedError(null)} className="w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center text-[#444444] hover:bg-[#F3F2F2] transition-colors shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                     <X size={16} />
                  </button>
               </div>
               <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div className="space-y-2">
                        <label className="text-[12px] font-semibold text-[#706E6B] ">Context</label>
                        <div className="p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] space-y-3">
                           <div className="flex justify-between border-b border-[#D8DDE6] pb-2">
                              <span className="text-[12px] text-[#444444] font-semibold">Method / Status</span>
                              <span className="text-[12px] text-[#080707] font-mono">{selectedError.method} / {selectedError.statusCode}</span>
                           </div>
                           <div className="flex justify-between border-b border-[#D8DDE6] pb-2">
                              <span className="text-[12px] text-[#444444] font-semibold">IP Address</span>
                              <span className="text-[12px] text-[#0176D3] font-mono">{selectedError.ip || '0.0.0.0'}</span>
                           </div>
                           <div className="space-y-1 pt-1">
                              <span className="text-[12px] text-[#444444] font-semibold block">Path Segment</span>
                              <div className="text-[12px] text-[#080707] break-all">{selectedError.path}</div>
                           </div>
                        </div>
                     </div>
                     <div className="space-y-2">
                        <label className="text-[12px] font-semibold text-[#706E6B] ">Organization</label>
                        <div className="p-4 rounded-md bg-[#F4F6F9] border border-[#D8DDE6] space-y-3">
                           <div className="flex justify-between border-b border-[#D8DDE6] pb-2">
                              <span className="text-[12px] text-[#444444] font-semibold">Subject Identity</span>
                              <span className="text-[12px] text-[#080707]">{selectedError.userId?.companyName || 'Unknown'}</span>
                           </div>
                           <div className="flex justify-between border-b border-[#D8DDE6] pb-2">
                              <span className="text-[12px] text-[#444444] font-semibold">Temporal Mark</span>
                              <span className="text-[12px] text-[#706E6B]">{new Date(selectedError.timestamp).toLocaleString()}</span>
                           </div>
                           <div className="space-y-1 pt-1">
                              <span className="text-[12px] text-[#444444] font-semibold block">Agent String</span>
                              <div className="text-[12px] text-[#080707] break-all leading-tight">{selectedError.userAgent || 'Unknown Agent'}</div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="space-y-2">
                     <label className="text-[12px] font-semibold text-[#C53030] ">Incident Payload</label>
                     <div className="p-4 rounded-md bg-[#FFF5F5] border border-[#FC8181] text-[#C53030] font-semibold text-[13px] leading-relaxed">
                        {selectedError.message}
                     </div>
                  </div>

                  {selectedError.stack && (
                     <div className="space-y-2">
                        <label className="text-[12px] font-semibold text-[#706E6B] ">Diagnostics Trace</label>
                        <div className="p-4 rounded-md bg-[#FAFAFA] border border-[#D8DDE6] text-[#444444] text-[12px] font-mono leading-relaxed whitespace-pre-wrap overflow-x-auto">
                           {selectedError.stack}
                        </div>
                     </div>
                  )}
               </div>
               <div className="px-6 py-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex justify-end gap-3">
                  <button 
                     className="px-4 py-2 rounded whitespace-nowrap border border-[#C9C9C9] bg-white text-[13px] font-semibold text-[#444444] hover:bg-[#F3F2F2]" 
                     onClick={() => setSelectedError(null)}
                  >
                     Dismiss
                  </button>
                  <button 
                     className="px-4 py-2 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-colors"
                     onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(selectedError, null, 2));
                        alertService.toast('success', 'Diagnostic payload copied to clipboard');
                     }}
                  >
                     Copy Payload
                  </button>
               </div>
            </Card>
         </div>
      )}
    </div>
  );
}
